import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Maximize, Newspaper, Heart } from "lucide-react";
import { api, BACKEND_URL } from "../lib/api";
import { BrandMark } from "../components/party/GameVisuals";
import { goFullscreen } from "../components/party/TvShare";

export const deviceId = () => {
  let d = localStorage.getItem("lif_device");
  if (!d) { d = `d_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`; localStorage.setItem("lif_device", d); }
  return d;
};

export function useNewsWall(slug, every = 20000) {
  const [wall, setWall] = useState(null);
  useEffect(() => {
    if (!slug) return undefined;
    const load = () => api.get(`/public/weddings/${slug}/news-wall?device_id=${deviceId()}`).then((r) => setWall(r.data)).catch((e) => setWall((w) => (e?.response?.status === 404 ? { missing: true, cards: [] } : w || { error: true, cards: [] })));
    load();
    const t = setInterval(load, every);
    return () => clearInterval(t);
  }, [slug, every]);
  return [wall, setWall];
}

// Walks the loved-weighted playlist; returns the index of the card to show.
export function useRotate(wall, ms = 8000) {
  const [i, setI] = useState(0);
  useEffect(() => { const t = setInterval(() => setI((x) => x + 1), ms); return () => clearInterval(t); }, [ms]);
  const cards = wall?.cards || [];
  const list = wall?.playlist?.length ? wall.playlist : cards.map((c) => c.item_id);
  if (!cards.length) return 0;
  return Math.max(0, cards.findIndex((c) => c.item_id === list[i % list.length]));
}

export default function NewsWall() {
  const { slug } = useParams();
  const [wall] = useNewsWall(slug);
  const cards = wall?.cards || [];
  const i = useRotate(wall);
  const upload = `${window.location.origin}/site/${slug}/news`;
  if (wall?.missing) return <div className="h-screen bg-[#0b0b0b] text-white flex items-center justify-center font-serif text-4xl" data-testid="news-wall-not-found">Wedding not found</div>;
  return (
    <div className="h-screen bg-[#0b0b0b] text-white flex flex-col overflow-hidden" data-testid="news-wall">
      <button onClick={goFullscreen} className="fixed bottom-16 right-4 z-20 opacity-40 hover:opacity-100 text-xs px-3 py-2 rounded-full bg-white/10 flex items-center gap-1" data-testid="news-wall-fullscreen"><Maximize className="w-3.5 h-3.5" /> Full screen</button>
      <div className="flex items-center justify-between px-10 pt-6">
        <div><BrandMark big /><p className="font-script text-[#C8A96B] text-4xl mt-1">{wall?.couple_names}</p></div>
        <div className="text-center"><div className="bg-white p-2 rounded-xl inline-block"><QRCodeSVG value={upload} size={96} /></div><p className="text-[10px] uppercase tracking-[0.25em] mt-1 text-white/60">Scan to heart & share</p></div>
      </div>
      <div className="flex-1 relative flex items-center justify-center min-h-0 p-6">
        {cards.length === 0 && <p className="font-serif text-5xl text-white/60 text-center max-w-3xl" data-testid="news-wall-empty">No breaking news yet. Snap a photo, add a headline and hit share!</p>}
        {cards.map((c, k) => (
          <img key={c.item_id} src={`${BACKEND_URL}${c.card_url}`} alt={c.headline} data-testid={k === i ? "news-wall-current" : undefined}
            className={`absolute max-h-full max-w-full aspect-square object-contain rounded-2xl shadow-2xl transition-[opacity,transform] duration-1000 ${k === i ? "opacity-100 scale-100" : "opacity-0 scale-95"}`} />
        ))}
        {cards[i]?.hearts > 0 && <div className="absolute top-4 right-10 flex items-center gap-2 bg-black/70 rounded-full px-5 py-2 text-2xl" data-testid="news-wall-hearts"><Heart className="w-6 h-6 fill-[#d41a24] text-[#d41a24]" /> {cards[i].hearts}</div>}
      </div>
      <div className="bg-[#d41a24] h-12 flex items-center overflow-hidden whitespace-nowrap" data-testid="news-wall-ticker">
        <span className="bg-white text-[#d41a24] font-bold px-4 h-full flex items-center gap-2 shrink-0 z-10"><Newspaper className="w-4 h-4" /> BREAKING</span>
        <div className="animate-[ticker_40s_linear_infinite] font-bold uppercase tracking-wide pl-6 flex shrink-0">
          {[0, 1].flatMap((r) => (cards.length ? cards : [{ headline: "Guests: share your photos to appear here" }]).map((c, k) => <span key={`${r}-${k}`} className="mx-8">{c.headline}</span>))}
        </div>
      </div>
    </div>
  );
}
